/**
 * Pure helpers for the reservation self-service manage flow
 * (see {@link ReservationStatusPage}). Extracted so the mutating logic
 * (reschedule target construction, error-code mapping, guarantee gating)
 * is unit-testable without rendering the component.
 */

/** Guarantee states for which the backend blocks self-service modify. */
const GUARANTEED_STATES = new Set(['paid', 'pending_payment', 'requested']);

/**
 * Whether a reservation's guarantee status blocks self-service modify.
 * The backend rejects modify for these states; the UI hides the modify
 * controls when this is true.
 */
export function isGuaranteed(guaranteeStatus?: string): boolean {
  const status = guaranteeStatus?.trim();
  return !!status && GUARANTEED_STATES.has(status);
}

/**
 * Build the `newResTime` value for a same-day reschedule.
 *
 * `resDateRaw` is the reservation's stored date string (`resDate.date`,
 * "YYYY-MM-DD HH:MM:SS"). We take only its YYYY-MM-DD portion and combine
 * it with the chosen `hour` ("HH:mm") to produce "YYYY-MM-DD HH:mm".
 *
 * The date always comes from the reservation, never from the slot — the
 * backend only supports same-day reschedule and 422s on a date change, so
 * a slot from a different day can never produce a cross-date value here.
 * Returns null when the reservation date can't be parsed (the caller then
 * skips the reschedule).
 */
export function buildRescheduleDateTime(
  resDateRaw: string,
  hour: string,
): string | null {
  const ymd = resDateRaw.split(' ')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return `${ymd} ${hour}`;
}

/**
 * Map a backend `serverCode` (the machine-readable `status` on a 4xx
 * reservation-action response) to its i18n message key. Unknown or absent
 * codes fall back to the generic error key.
 */
export function messageKeyForServerCode(serverCode?: string): string {
  switch (serverCode) {
    case 'no_availability':
      return 'RESERVATION_STATUS_MODIFY_ERROR_NO_AVAILABILITY';
    case 'past_cutoff':
      return 'RESERVATION_STATUS_MODIFY_ERROR_PAST_CUTOFF';
    case 'guaranteed_modify_disabled':
      return 'RESERVATION_STATUS_MODIFY_ERROR_GUARANTEED';
    case 'not_modifiable':
      return 'RESERVATION_STATUS_MODIFY_ERROR_NOT_MODIFIABLE';
    case 'invalid_input':
      return 'RESERVATION_STATUS_MODIFY_ERROR_INVALID_INPUT';
    case 'date_change_unsupported':
      return 'RESERVATION_STATUS_MODIFY_ERROR_DATE_UNSUPPORTED';
    default:
      return 'RESERVATION_STATUS_ERROR';
  }
}
